(ns quuppa.qmd.steps.identification
  (:require [goog.object :as gobj]
            [re-frame.core :as rf]
            ["react" :as react]
            [quuppa.peweb.query :as q]
            [quuppa.qmd.code-reader-utils :as utils]
            [quuppa.qmd.layout :as layout]
            [quuppa.qmd.state :as state]
            [quuppa.qmd.events :as e]
            [quuppa.qmd.subs :as s]
            [quuppa.qmd.code-reader :as code-reader]
            [quuppa.qmd.steps.core :as core]
            [quuppa.ui.button :as button]
            [quuppa.ui.icons :refer [icon image]]
            [quuppa.qmd.steps.identification.events :as events]))

(rf/reg-event-db ::open-camera
  (fn [db [_ locator]]
    (update db ::core/db assoc
            :state :scan-locator-code
            :locator locator)))

(rf/reg-event-db ::ask-permission
  (fn [db _]
    (update db ::core/db assoc :state :ask-permission)))

;; Store flag to localStorage if we want successfully opened
;; camera previously.
;; We use this to only display "Enable camera" screen before
;; asking the user for permission the first time.
;; At least on Safari/iOS.
;; On Chrome we can just query the permission status.
(def CAMERA-USED-PREVIOUSLY (str ::camera-used-previously))

(defn mark-camera-used! []
  (js/localStorage.setItem CAMERA-USED-PREVIOUSLY "1"))

(rf/reg-event-fx ::start-scan
  (fn [_ [_ locator]]
    (let [camera-used-previously (js/localStorage.getItem CAMERA-USED-PREVIOUSLY)]
      ;; If possible (on Chrome) check if we really have
      ;; the camera permission and go directly to
      ;; camera view or error screen.
      (if (and (exists? js/navigator)
               (some-> js/navigator .-permissions .-query))
        (do
          (-> (.. js/navigator -permissions (query #js {:name "camera"}))
              (.then (fn [result]
                       (case (.-state result)
                         "granted" (rf/dispatch [::open-camera locator])
                         "denied" (rf/dispatch [::no-camera-permission])
                         (rf/dispatch [::ask-permission])))))
          {})
        ;; If we can't query the permission, check if we have previously
        ;; opened the camera view successfully.
        ;; If yes, go to camera. If not, show "Enable Camera" screen.
        (if camera-used-previously
          {:fx [[:dispatch [::open-camera locator]]]}
          {:fx [[:dispatch [::ask-permission]]]})))))

(rf/reg-event-db ::no-camera-permission
  (fn [db _]
    (update db ::core/db assoc
            :state :error
            :error {:reason :no-camera-permission})))

(rf/reg-event-fx ::save-id
  (fn [{:keys [db]} [_ locator scanned-locator-id]]
    (let [{:keys [token]} (-> db :quuppa.routing/match :parameters :path)]
      {:fx [[:server-dispatch {:event [:deployment/set-locator-id
                                       {:deployment-token token
                                        :from-id (:id locator)
                                        :to-id scanned-locator-id
                                        :done? true}]
                               :timeout  30000
                               :on-success [::events/locator-id-set (:name locator)]
                               :on-error   [::events/locator-id-failure]}]]})))

(rf/reg-event-fx ::assign-locator-id
  (fn [{:keys [db]} [_ locator {:keys [device-id] :as parsed}]]
    (let [error   (utils/validate-locator-code parsed)
          {:keys [token]} (-> db :quuppa.routing/match :parameters :path)
          project (:result (q/get-result db e/project-query {:token token}))
          used-by (state/is-locator-id-used? project device-id)
          ;; Ignore error if the same ID scanned as this locator is already using
          used-by (when (not= (:name locator) (:locator-name used-by))
                    used-by)]
      (cond
        used-by
        {:db (-> db
                 (update ::core/db assoc
                         :state :error
                         :error {:reason    :locator-id-used
                                 :used-by   used-by
                                 :device-id device-id}))}

        error
        {:db (-> db
                 (update ::core/db assoc
                         :state :error
                         :error {:err-msg "Invalid Locator ID"}))}

        :else
        {:fx [[:dispatch [::save-id locator device-id]]]}))))


(rf/reg-event-fx ::result
  (fn [{:keys [db]} [_ code _full-decode-result]]
    (js/console.debug "Code decode result from "
                      (gobj/getValueByKeys _full-decode-result #js ["result" "debugData" "decoderName"])
                      ":" code _full-decode-result)
    (let [locator (:locator (::core/db db))
          {:keys [device-id] :as m} (utils/parse-locator-code code)
          error   (utils/validate-locator-code m)]
      ;; If code didn't contain valid ID, go to error:
      ;; (not much validation done right now)
      (if (and device-id
               (nil? error))
        {:fx [[:dispatch [::assign-locator-id locator m]]]}
        {:db (-> db
                 (update ::core/db assoc
                         :state :error
                         :error {:reason :scan-error
                                 :code   code}))}))))

(rf/reg-event-fx ::error
  (fn [{:keys [db]} [_ error]]
    ;; ignore errors if we have left the scanning page (id not present anymore)
    (when (or force (::core/db db))
      {:db (-> db
               (update ::core/db assoc
                       :state :error
                       :error error))})))

(rf/reg-event-fx ::confirm
  (fn [{:keys [db]} [_ locator]]
    (let [{:keys [token]} (-> db :quuppa.routing/match :parameters :path)]
      {:fx [[:server-dispatch {:event [:deployment/set-locator-id
                                       {:deployment-token token
                                        :from-id (:id locator)
                                        :done? true}]
                               :on-success [::confirm-done (:name locator)]
                               :on-error [::locator-id-failure]}]]})))


(rf/reg-event-fx ::confirm-done
  (fn [_ [_ _locator-name]]
    {:fx [[:dispatch [::core/clear]]
          [:dispatch [:nav/update-query assoc :step "installation"]]]}))

(rf/reg-event-db ::manual-locator-id
  (fn [db [_ locator]]
    (update db ::core/db assoc
            :state :manual-id-input
            :locator locator)))

(rf/reg-event-db ::init
                 (fn [db [_ _]]
                   (update db ::core/db assoc
                           :state :init)))

(defn identification-view-container [& children]
  ;; this was justify-content-center before, but I like the evenly better, gives more 'room' to the view, especially
  ;; as QMD is used mainly with mobiles
  (into [:div.container.d-flex.flex-column.flex-grow-1.justify-content-evenly.align-items-center]
        children))

(defn scan-button [{:keys [init locator]}]
  [button/button
   {:class [(if init "btn-primary" "btn-dark")]
    :large true
    :on-click #(rf/dispatch [::start-scan locator])
    ;; could be just :upc-scan, but seems in 'init' case we want to have icon to be 'primary' color, not --bs-btn-color.
    :start-icon [icon :upc-scan.mr-3.icon-md
                 {:class [(if init "text-white" "text-primary")]}]}
   "Scan ID"])

(defn confirm-button [{:keys [error token locator]}]
  (let [used-locator-name (->> error
                               :used-by
                               :locator-name)
        used-locator-id (->> error
                             :used-by
                             :locator-id)
        scanned-locator-id (:device-id error)]

  [button/button
   {:class "btn-primary"
    :large true
    :on-click #(rf/dispatch [::events/reset-locator-id-and-save
                              {:token token
                               :used-locator-name used-locator-name
                               :used-locator-id used-locator-id
                               :locator locator
                               :scanned-locator-id scanned-locator-id}])}
   "Confirm"]))

(defn manual-id-button [{:keys [locator variant]
                         :or {variant "secondary"}}]
  [button/button
   {:large      true
    :variant    variant
    :start-icon :input-cursor
    :on-click   #(rf/dispatch [::manual-locator-id locator])}
   "Enter ID manually"])

(defn id-display [{:keys [locator-id on-click locator]}]
  [:<>
   [identification-view-container
    [:div
     [layout/qmd-image
      {:image (image "qmd_identification_2_success")}]
     [:h2.my-2 "Success!"]]

    [:span.text-center
     [:p "Locator ID is saved: "]
     [:div.h5.font-monospace locator-id]]

    [button/stack
     {:class  "wizard-buttons"
      :center true}
     (when (state/pending? locator)
       [:<>
        [scan-button
         {:init    false
          :locator locator}]
        [manual-id-button
         {:locator locator
          :variant "dark"}]])

     [:button.btn.btn-primary.btn-lg
      {:on-click on-click}
      "Next step"]]]])

(defn id-input-form [locator]
  (let [[id set-id!] (react/useState (if-not (:has-dummy-id locator) (:id locator) ""))
        [submitting? set-submitting!] (react/useState false)]
    [:form
     {:on-submit (fn [e]
                   (.preventDefault e)
                   (set-submitting! true)
                   (rf/dispatch [::assign-locator-id locator (utils/parse-locator-code id)]))}
     [:div.form-group
      [:label.form-label.visually-hidden {:for "locator-id"} "Locator ID"]
      [:input.form-control.form-control-lg
       {:name        "locator-id"
        :title       "Locator ID is alphanumeric string(a-f, 0-9) of 12 characters."
        :placeholder "E.g: abcd1234abcd"
        :pattern     "^[0-9A-Fa-f]{12}$"
        :min-length  "12"
        :on-change   #(set-id! (-> % .-target .-value))
        :value       id}]]
     [:div.form-group.d-flex.flex-row.justify-content-between
      [button/button
       {:attr     {:type "submit"}
        :disabled (or (not= 12 (count id))
                      submitting?)}
       "Submit"]
      [button/button
       {:attr     {:type "button"}
        :variant  "secondary"
        :on-click #(rf/dispatch [::core/clear])}
       "Cancel"]]]))

(defn locator-code-scan []
  [:div.d-flex.flex-column.flex-grow-1.justify-content-between.align-items-center
   {:key "video-parent"}
   [:f> code-reader/code-reader
    {:on-init #(mark-camera-used!)
     :on-error #(rf/dispatch [::error %])
     :on-result #(rf/dispatch [::result %1 %2])}]])

(defn enable-camera [{:keys [locator]}]
  [identification-view-container
   [layout/qmd-image
    {:image (image "qmd_identification_2_camera")}]

   [:h5.my-2
    "Enable Camera"]

   [:p.my-2.text-center
    {:style {:max-width "75%"}}
    "You need to allow access to camera in order to continue with the deployment."]

   [:div.d-flex.flex-column.wizard-buttons.container.align-items-center
    [:button.btn.btn-lg.btn-primary
     {:on-click #(rf/dispatch [::open-camera locator])}
     "Allow"]]])

(defn camera-permission-error []
  [identification-view-container
   [layout/qmd-image
    {:image (image "qmd_identification_2_camera")}]

   [:h5.my-2 "Enable Camera"]

   [:p.my-2.text-center
    {:style {:max-width "75%"}}
    "Please, go to your Browser Settings and allow access to camera in order to continue with the deployment."]])

(defn scan-error [{:keys [locator error]}]
  [:div.container.d-flex.flex-column.flex-grow-1.justify-content-evenly.align-items-center
   {:style {"--bs-gutter-x" "2rem"}}
   [layout/qmd-image
    {:image (image "qmd_identification_2_warning")}]

   [:h3.my-2 "Ooops!"]

   [:p.my-2.text-center
    "This is not a valid Quuppa Locator code. Try scanning another one."]
   [:div.d-flex.flex-column.align-items-center
    [:p.mb-2 [:em "Decoded code"]]
    [:code (:code error)]]

   [button/stack
    {:class  "wizard-buttons"
     :center true}
    [scan-button
     {:init    true
      :locator locator}]
    [manual-id-button {:locator locator}]]])

(defn locator-id-used [{:keys [error token locator]}]
  [:<>
   [identification-view-container
    [layout/qmd-image
     {:image (image "qmd_identification_2_warning")}]
    [:<>
     [:h2.my-2 "Ooops!"]
     [:p
      (str "This ID is already in use by locator "
           (:locator-name (:used-by error))
           ". Do you want to add the ID to new locator? The previous locator will be reset.")]]

    [button/stack
     {:class  "wizard-buttons"
      :center true}
     [confirm-button {:error   error
                      :token token
                      :locator locator}]]]])

(defn identification-error [locator error token]
  (case (:reason error)
    :no-camera-permission
    [camera-permission-error]

    :scan-error
    [scan-error
     {:locator locator
      :error   error}]

    :locator-id-used
    [locator-id-used
     {:error error
      :locator locator
      :token token}]

    [identification-view-container
     [layout/qmd-image
      {:image (image "qmd_identification_2_warning")}]

     [:h5.my-2 (:err-msg error)]]))

(defn identification-header [locator data token]
  (case (:state data)
    :scan-locator-code
    [layout/wizard-top-bar
     {:text "Keep the code within the viewfinder"
      :on-close #(rf/dispatch [::core/clear])}]

    [layout/wizard-navbar
     {:locator locator
      :token token
      :step :identification
      :on-back #(rf/dispatch [::core/close-wizard])}]))

(defn identification [{:keys [token cs-id locator-name]}]
  (let [locator @(rf/subscribe [::s/locator-by-name token cs-id locator-name])
        {:keys [state error] :as data} @(rf/subscribe [::core/data])

        id-saved? (:target-id (:identification (:deployment-data locator)))
        identification-done? (state/identified? locator)
        state (or state
                  (cond
                    identification-done? :id-display
                    id-saved? :confirm
                    :else :init))]
    [:div.panel.qmd-wizard.d-flex.flex-column
     [:div.d-flex.flex-column.flex-grow-1
      [identification-header locator data token]

      (case state
        :error
        [identification-error locator error token]

        :init
        [:<>
         [identification-view-container
          [layout/qmd-image
           {:image (image "qmd_identification_1")}]
          [button/stack
           {:class  "wizard-buttons"
            :center true}
           [scan-button
            {:init true
             :locator locator}]
           [manual-id-button {:locator locator}]]]]

        :ask-permission
        [enable-camera
         {:locator locator}]

        :scan-locator-code
        [:f> locator-code-scan]

        ;; Confirm scan result
        :confirm
        [id-display
         {:locator locator
          :locator-id (or (:target-id (:identification (:deployment-data locator)))
                          (:id locator))
          :on-click #(rf/dispatch [::confirm locator])}]

        ;; Input ID manually sc-34323
        :manual-id-input
        [:<>
         [identification-view-container
          [layout/qmd-image
           {:image (image "qmd_identification_2_camera")}]

          [:div
           [:h2 "Locator ID"]
           [:p "Please input the locator ID manually."]

           [:f> id-input-form locator]]]]

        ;; Display saved ID
        :id-display
        [id-display
         {:locator locator
          :locator-id (or (:target-id (:identification (:deployment-data locator)))
                          (:id locator))
          :on-click #(rf/dispatch [::confirm locator])}]

        [layout/spinner-panel])]]))

(comment
  (rf/dispatch [:notifications/notify {:message "Saved"
                                       :class "alert-qmd"
                                       :timeout-ms (* 25 1000)}]))
